import {Box, BoxProps, Typography} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {Link, useLocation} from "react-router-dom";
import {palette} from "@/constants";

const DEFAULT_MOBILE_EXCLUDED_TABS = ["Content Manager"] as const;

interface ICustomProps<pages extends string | number | symbol> {
	routesMap: { [key in pages]: string },
	icons: JSX.Element[],
	slimLabels: string[],
	height: string,
	/**
	 * Callbacks for tabs that trigger an action instead of navigating (e.g. Frank / Voiceflow chat).
	 */
	tabActions?: Partial<{ [key in pages]: () => void }>,
	/**
	 * Tab keys to hide from the mobile bottom bar. Defaults to ["Content Manager"].
	 */
	mobileExcludedTabs?: readonly string[],
}

const useStyles = makeStyles<{ height: string }>()((theme, {height}) => ({
	mobileNavBar: {
		background: palette.black,
		borderRadius: "20px 20px 0 0",
		height,
		position: "fixed",
		bottom: 0,
		width: "100%",
		display: "flex",
		padding: "10px",
		boxSizing: "border-box",
		justifyContent: "space-around",
		zIndex: 10,
		[theme.breakpoints.up("lg")]: {
			display: "none"
		}
	},
	tab: {
		width: "45px",
		textAlign: "center",
		"& svg": {
			"& path": {
				fill: palette.midGrey
			},
			width: "24px",
			marginTop: "5px",
			height: "24px"
		},
		"&.active": {
			"& svg path": {
				fill: palette.orange
			},
			"& p": {
				color: theme.palette.common.white,
				fontWeight: 600
			}
		}
	},
	label: {
		fontSize: "12px",
		lineHeight: 1.1,
		color: palette.midGrey
	},
	labelBeta: {
		fontSize: "9px",
		display: "block",
		lineHeight: 1.2
	},
	navIcon: {
		marginTop: "3px",
		maxWidth: "20px",
		display: "inline-block"
	}
}));

type IProps<T extends string | number | symbol> = ICustomProps<T> & Omit<BoxProps, "children">;

export const MobileNavBar = <pages extends string | number | symbol>({routesMap, slimLabels, height, icons, tabActions, mobileExcludedTabs = DEFAULT_MOBILE_EXCLUDED_TABS, className, ...props}: IProps<pages>): JSX.Element => {
	const location = useLocation();
	const {classes, cx} = useStyles({height});

	let pageSelected: pages | undefined = undefined;
	Object.keys(routesMap).forEach(e => {
		const page = e as pages;
		if (location.pathname.startsWith(routesMap[page]))
			pageSelected = page;
	})

	return (
		<Box className={cx(classes.mobileNavBar, className)} {...props}>
			{(Object.keys(routesMap) as pages[]).filter(e => !mobileExcludedTabs.includes(e as string)).map((tab, i) => {
				const action = tabActions?.[tab];
				return (
					<Box className={cx(classes.tab, (pageSelected === tab) ? "active" : "")} key={`mobile_nav_bar_icon${i}`}>
						{action ? (
							<Box component="div" onClick={action} sx={{cursor: "pointer"}}>
								{icons[i]}
							</Box>
						) : (
							<Link to={routesMap[tab] ?? ""}>
								{icons[i]}
							</Link>
						)}
						<Typography className={classes.label}>
							{slimLabels[i].includes("(BETA)") ? (
								<>{slimLabels[i].replace(" (BETA)", "")}<span className={classes.labelBeta}>(BETA)</span></>
							) : slimLabels[i]}
						</Typography>
					</Box>
				);
			})}
		</Box>
	);
};
